import { useQuery } from '@tanstack/react-query';
import { isNil } from 'lodash';
import { DailyNavRecord } from 'api/models/DailyNavRecord';
import { FirmsService } from 'api/services/FirmsService';
import {
  DAILY_NAV_RECORDS_BY_FIRM_DEFAULT_ERROR_MESSAGE,
  DAILY_NAV_RECORDS_BY_FIRM_QUERY_KEY,
} from 'common/constants/services/firms';
import { throwError } from 'utilities';
import { GetDailyNavRecordsReturn, UseGetDailyNavRecordsParams } from './useGetDailyNavRecords.types';

const getDailyNavRecords = async (firmId: number, date?: string): Promise<DailyNavRecord[] | null> => {
  try {
    const result = await FirmsService.apiFirmsDailyNavList(String(firmId), undefined, date);

    if (!result) {
      return throwError(DAILY_NAV_RECORDS_BY_FIRM_DEFAULT_ERROR_MESSAGE);
    }

    return result;
  } catch (error) {
    return throwError(DAILY_NAV_RECORDS_BY_FIRM_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetDailyNavRecords = (params: UseGetDailyNavRecordsParams): GetDailyNavRecordsReturn => {
  const { firmId, date, shouldQueryAutomatically = true } = params;

  const { data, error, isError, isFetching, isLoading, refetch } = useQuery<
    GetDailyNavRecordsReturn['data'],
    Error
  >({
    enabled: !isNil(firmId) && shouldQueryAutomatically,
    queryFn: () => getDailyNavRecords(firmId as number, date),
    queryKey: [DAILY_NAV_RECORDS_BY_FIRM_QUERY_KEY, firmId, date ?? 'effective-date'],
    // NAV comp data changes intraday, so it must never be shown stale: keep it out
    // of the IndexedDB persister (no rehydrating yesterday's response on load) and
    // always refetch on mount and when the tab regains focus.
    meta: { persist: false },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  return {
    data,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetDailyNavRecords;
export { getDailyNavRecords };
